<template>
  <div>
    <validation-observer ref="mfaRules">
      <b-form>
        <!-- Download MFA -->
        <b-row>
          <b-col
            md="6"
            class="mt-2"
          >
            <InputField
              id="master-financing-agreement"
              v-model="masterFinancingAgreement"
              label=""
              type="text"
              readonly
            />
          </b-col>
        </b-row>
        <!-- Download MFA -->
        <hr class="horizontal-line">

        <b-row class="match-height mb-2 mt-1">
          <b-col
            md="6"
          >
            <h6 class="section-label mb-1 mt-2">
              PFI Master Financing Agreement Document Upload
            </h6>
            <div
              v-if="!documentData.url"
              class="upload-image"
            >
              <Spinner :show="!!documentData.loading" />

              <validation-provider
                v-slot="{ errors }"
                :name="`${documentData.title || 'Document File'}`"
              >
                <b-form-group
                  :state="!errors.length"
                  :invalid-feedback="errors[0] || documentData.validationError"
                >
                  <b-form-file
                    v-model="documentData.file"
                    :state="!errors.length && !documentData.validationError"
                    placeholder="Choose a file or drop it here..."
                    drop-placeholder="Drop file here..."
                    accept=".jpg,.jpeg,.png, .docx,.pdf, application/pdf, .xlsx, .csv"
                    plain
                    @change="handleFileUpload($event, documentData)"
                  />

                  <!-- Instructions -->
                  <div
                    class="upload-instruction text-center"
                  >
                    <h4>{{ `${documentData.title || 'Upload Document File'}` }}</h4>
                    <p>Drag & drop file or choose from your device to upload</p>
                    <p class="format">
                      Format accepted: JPG, PNG, JPEG, DOCX, PDF, XLSX, CSV
                    </p>
                  </div>
                </b-form-group>
              </validation-provider>
            </div>
            <!-- Preview -->
            <DocumentViewer
              v-else-if="documentData.url || documentData.loading"
              :document-url="documentData.url"
              :loading="documentData.loading"
              class="embedded-document-viewer"
              @loaded="documentData.loading = false"
              @error="documentData.loading = false"
            />

            <div class="d-flex align-items-center justify-content-center">
              <b-button
                v-if="lmsRole === 'PFI' && documentData.url"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-1"
                :disabled="isReadonly"
                @click="removeDocument(documentData)"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-25"
                />
                <span>Remove</span>
              </b-button>
              <b-button
                v-if="documentData.url"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="ml-0"
                @click="handleDownload(documentData.url)"
              >
                <feather-icon
                  icon="DownloadIcon"
                  class="mr-25"
                />
                <span>Download</span>
              </b-button>
            </div>
          </b-col>
          <b-col
            v-if="lmsRole === 'Admin'"
            md="6"
          >
            <h6 class="section-label mb-1 mt-2">
              Institution Master Financing Agreement Document Upload
            </h6>
            <div
              v-if="!institutionDocumentData.url"
              class="upload-image"
            >
              <Spinner :show="!!institutionDocumentData.loading" />

              <validation-provider
                v-slot="{ errors }"
                :name="`${institutionDocumentData.title || 'Document File'}`"
                :rules="institutionDocumentData.fileRules"
              >
                <b-form-group
                  :state="!errors.length"
                  :invalid-feedback="errors[0] || institutionDocumentData.validationError"
                >
                  <b-form-file
                    v-model="institutionDocumentData.file"
                    :state="!errors.length && !institutionDocumentData.validationError"
                    placeholder="Choose a file or drop it here..."
                    drop-placeholder="Drop file here..."
                    accept=".jpg,.jpeg,.png,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                    plain
                    :disabled="sourceChannel === 'pendingApprovals' ? !isReadonly : isReadonly"

                    @change="handleFileUpload($event, institutionDocumentData)"
                  />

                  <!-- Instructions -->
                  <div
                    v-if="!institutionDocumentData.url"
                    class="upload-instruction text-center"
                  >
                    <h4>{{ `${institutionDocumentData.title || 'Upload Document File'}` }}</h4>
                    <p>Drag & drop file or choose from your device to upload</p>
                    <p class="format">
                      Format accepted: jpg, png, bmp, jpeg, docx, pdf
                    </p>
                  </div>

                </b-form-group>
              </validation-provider>
            </div>

            <!-- Preview -->
            <DocumentViewer
              v-else-if="institutionDocumentData.url || institutionDocumentData.loading"
              :document-url="institutionDocumentData.url"
              :loading="institutionDocumentData.loading"
              class="embedded-document-viewer"
              @loaded="institutionDocumentData.loading = false"
              @error="institutionDocumentData.loading = false"
            />
            <div class="d-flex align-items-center justify-content-center mt-2">

              <b-button
                v-if="lmsRole === 'Admin' && sourceChannel === 'pendingApprovals' && institutionDocumentData.url && getUserPermission('APPROVE_MFA_DOCUMENT')"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="ml-0"
                @click="removeDocument(institutionDocumentData)"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-25"
                />
                <span>Remove</span>
              </b-button>
              <b-button
                v-if="institutionDocumentData.url"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="ml-2"
                @click="handleDownload(institutionDocumentData.url)"
              >
                <feather-icon
                  icon="DownloadIcon"
                  class="mr-25"
                />
                <span>Download</span>
              </b-button>
            </div>
            <div
              v-if="lmsRole === 'Admin' && sourceChannel === 'pendingApprovals'"
              class="d-flex align-items-center justify-content-center mt-4"
            >
              <Button
                v-if="getUserPermission('APPROVE_MFA_DOCUMENT')"
                label="Update Document"
                size="md"
                @click="handleMFASubmit"
              />
            </div>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
    <FullScreenSpinner :show="showSpinner" />
  </div>
</template>
<script>
/* eslint-disable no-param-reassign */
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BRow, BCol, BForm, BFormGroup, BFormFile, BButton,
} from 'bootstrap-vue'
import { v4 as uuidv4 } from 'uuid'
import { permissionMixin } from '@core/mixins/permission'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { toastAlert } from '@core/mixins/toast'
import { mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import * as FileSaver from 'file-saver'
import Spinner from '@/views/components/spinner/Spinner.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../InputField.vue'
import Button from '../../components/button/Button.vue'
import { pdfIcon, wordIcon } from '@/helpers/data'
import { lmsPermissions } from '../../../helpers/permissions'
import DocumentViewer from '@/views/components/DocumentViewer.vue'

export default {
  name: 'MasterFinancingAgreement',
  components: {
    Button,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormFile,
    ValidationObserver,
    ValidationProvider,
    Spinner,
    FullScreenSpinner,
    InputField,
    BButton,
    DocumentViewer,
  },
  directives: {
    Ripple,
  },
  mixins: [permissionMixin, tableMixins, sweetAlert, toastAlert],
  props: {
    // isReadonly: {
    //   type: Boolean,
    //   default: false,
    // },
    initialFormData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    mfaFormData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    userType: {
      type: String,
      default: '',
    },
    channel: {
      type: String,
      default: '',
    },

  },
  data() {
    return {
      isReadonly: false,
      pdfIcon,
      wordIcon,
      mfaButton: 'Save',
      masterFinancingAgreement: 'Master Financing Agreement',
      documentData: {
        title: 'PFI Master Financing Agreement',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
        uploadType: 'PFI',
      },
      institutionDocumentData: {
        title: 'Institution Master Financing Agreement',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
        fileRules: 'required',
        uploadType: 'Institution',
      },
      fileRules: 'required',
      showSpinner: false,
      documentLoadSpinner: false,
      currentPartnerCode: '',
      uploadType: '',
      lmsRole: '',
      sourceChannel: '',
    }
  },
  watch: {
    isEdit: {
      immediate: true,
      handler(val) {
        this.isReadonly = val
        this.isDisabled = val
      },
    },
    userType: {
      immediate: true,
      handler(val) {
        this.lmsRole = val
      },
    },
    channel: {
      immediate: true,
      handler(val) {
        this.sourceChannel = val
      },
    },

    initialFormData: {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.resetDocumentField(this.documentData)
        this.resetDocumentField(this.institutionDocumentData)
        if (newValue && Object.keys(newValue).length) {
          if (Array.isArray(newValue.documents) && newValue.documents.length > 0) {
            this.documentData = {
              ...newValue.documents[0],
              url: newValue.documents[0].url,
              fileExtension: newValue.documents[0].fileExtension,
              documentId: newValue.documents[0].documentId,
            }
          } else {
            this.documentData = {
              url: newValue.url || '',
              fileExtension: newValue.fileExtension || '',
              documentId: newValue.documentId || '',
              loading: !!newValue.url,
            }
          }

          this.currentPartnerCode = newValue.partnerCode
          this.uploadType = 'Institution'
          this.getPartnerDocuments(newValue.partnerCode)
        }
        this.emitFormUpdate()
      },
    },
    documentData: {
      handler() {
        this.emitFormUpdate()
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      uploadDocumentImages: 'account/uploadDocumentImagesAction',
      uploadMFADocument: 'account/uploadMFADocument',
      deleteDocumentsUpload: 'account/deleteDocumentsUploadAction',
      deleteMFADocumentsUpload: 'account/deleteMFADocumentsUploadAction',
      getMasterFinancingAgreement: 'account/getMasterFinancingAgreement',
    }),
    emitFormUpdate() {
      const formData = this.getFormData()
      this.$emit('update', {
        formData: { ...formData },
      })
    },
    resetDocumentField(docData) {
      docData.file = null
      docData.url = ''
      docData.fileExtension = ''
      docData.loading = false
      docData.validationError = ''
      docData.documentId = ''
    },
    getFormData() {
      const formData = {
        partnerCode: this.currentPartnerCode,
        documents: [
          {
            type: 'MasterFinancingAgreement',
            url: this.documentData.url,
            fileExtension: this.documentData.fileExtension,
            documentId: this.documentData.documentId,
          },
        ],
      }
      return formData
    },
    handleMFASubmit() {
      return new Promise((resolve, reject) => {
        this.$refs.mfaRules.validate().then(async success => {
          if (success) {
            resolve(true)
            try {
              this.showSpinner = true
              const pfiFormData = this.getFormData()

              const documentFormData = {
                partnerCode: this.currentPartnerCode,
                documents: [
                  {
                    type: 'MasterFinancingAgreement',
                    url: this.institutionDocumentData.url,
                    fileExtension: this.institutionDocumentData.fileExtension,
                    documentId: this.institutionDocumentData.documentId,
                  },
                ],
              }
              const formData = this.uploadType === 'PFI' ? pfiFormData : documentFormData

              const response = await this.uploadMFADocument({ formData, permission: lmsPermissions.APPROVE_MFA_DOCUMENT })
              const { status } = response
              if (status === 200) {
                this.alertSuccess({
                  text: 'Your request to Save Master Financing Agreement was successful',
                })
                this.showSpinner = false
              }
            } catch (error) {
              error.response.data.errors.forEach(responseError => {
                this.alertError({ text: `Your request to Save Master Financing Agreement failed. Reason: ${responseError.errorMessage}` })
              })
            } finally {
              this.showSpinner = false
            }
          } else {
            reject()
          }
        })
        this.emitFormUpdate()
      })
    },

    async removeDocument(targetDocumentData) {
      try {
        targetDocumentData.loading = true
        this.documentLoadSpinner = true
        const formData = { documentId: targetDocumentData.documentId }
        const response = await this.deleteMFADocumentsUpload({
          formData,
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
        })
        const { status } = response
        if (status === 200) {
          this.alertSuccess({ text: 'Document Removed' })
          this.resetDocumentField(targetDocumentData)
        }
      } catch (error) {
        if (Array.isArray(error.response.data.errors)) {
          error.response.data.errors.forEach(responseError => {
            this.alertError({
              text: `Delete failed, Reason: ${responseError.errorMessage}`,
            })
          })
        } else {
          this.alertError({
            text: `Delete failed, Reason: ${error}`,
          })
        }
      } finally {
        targetDocumentData.loading = false
      }
    },
    getFileExtension(url) {
      try {
        const urlObj = new URL(url)
        const params = new URLSearchParams(urlObj.search)

        const documentId = params.get('DocumentId')
        if (!documentId) return null

        const parts = documentId.split('.')
        return parts.length > 1 ? parts.pop() : null
      } catch (err) {
        console.error('Invalid URL:', err)
        return null
      }
    },

    async handleDocumentDownload(url) {
      try {
        this.isBusy = true

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: '*/*',
          },
        })

        if (response.ok) {
          const blob = await response.blob()
          // Extract filename from URL or use title
          const filename = new URL(url).pathname.split('/').pop()
          const extension = this.getFileExtension(url)
          if (extension === 'document') {
            FileSaver.saveAs(blob, `${filename}.docx`)
          } else if (extension === 'sheet') {
            FileSaver.saveAs(blob, `${filename}.xlsx`)
          } else {
            FileSaver.saveAs(blob, `${filename}.csv`)
          }
          this.alertSuccess({
            text: 'Your request to download file was successful',
          })
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    handleDownload(url) {
      try {
        const extension = this.getFileExtension(url)
        if (extension === 'document' || extension === 'sheet' || extension === 'csv') {
          this.handleDocumentDownload(url)
        } else {
          const link = document.createElement('a')
          link.href = url

          // Extract filename from URL or use title
          const filename = new URL(url).pathname.split('/').pop()
      || this.viewDocumentModalTitle.replace(/\s+/g, '_')

          link.setAttribute('download', filename)
          link.style.display = 'none'
          document.body.appendChild(link)

          // Trigger download
          link.click()
          this.alertSuccess({ text: 'Your request to download file was successful' })

          // Clean up
          document.body.removeChild(link)
        }
      } catch (error) {
        this.alertError({ text: 'Download may have been blocked. Please check your browser.' })
      }
    },
    validateFile(file, maxSizeMB = 5) {
      const validTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/bmp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ]

      if (!file) {
        return 'Please select a file.'
      }

      if (!validTypes.includes(file.type)) {
        return 'Unsupported file type.'
      }

      if (file.size > maxSizeMB * 1024 * 1024) {
        return `File size exceeds ${maxSizeMB}MB limit.`
      }

      return null
    },
    async handleFileUpload(event, targetDocumentData) {
      this.uploadType = targetDocumentData.uploadType

      const { files } = event.target
      targetDocumentData.validationError = ''

      const validationError = this.validateFile(files && files[0])
      if (validationError) {
        targetDocumentData.validationError = validationError
        return
      }

      const file = files[0]

      try {
        targetDocumentData.loading = true

        const formData = new FormData()
        formData.append('attachedFile', file)
        formData.append('type', 'Others')
        formData.append('referenceNumber', uuidv4())

        const response = await this.uploadDocumentImages({
          formData,
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
        })

        const { status, data } = response
        if (status === 200 && data) {
          targetDocumentData.url = data.url
          targetDocumentData.documentId = data.id
          targetDocumentData.fileExtension = data.fileExtension

          targetDocumentData.validationError = ''
          // targetDocumentData.uploadType = ''
          this.toastAlert({
            title: this.$t('Document uploaded successfully'),
          })
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          error.response.data.errors.forEach(err => {
            this.alertError({
              text: `Upload failed: ${err.errorMessage}`,
            })
          })
        } else {
          this.alertError({
            text: `Upload failed: ${error.message || 'An unknown error occurred.'}`,
          })
        }
      } finally {
        targetDocumentData.loading = false
      }
    },
    async getPartnerDocuments(partnerCode) {
      try {
        this.institutionDocumentData.loading = true
        const response = await this.getMasterFinancingAgreement({
          partnerCode,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })
        const { status, data } = response
        if (status === 200 && data) {
          const filterPartnerDocumentByVersion = data.filter(item => item.version === 'Admin' && item.name === 'MasterFinancingAgreement')
          const dataItem = filterPartnerDocumentByVersion.find(val => val.name === 'MasterFinancingAgreement')
          this.institutionDocumentData = {
            url: dataItem.url,
            fileExtension: dataItem.fileExtension,
            documentId: dataItem.documentId,
            loading: !!dataItem.url,
          }
          this.institutionDocumentData.loading = false
        }
      } catch (error) {
        // console.log(error);
      } finally {
        this.institutionDocumentData.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-image{
    border: 2px dashed #DCD9D9;
    min-height: 500px;
    position: relative;
    width: 100%;
}
.horizontal-line {
  position: relative;
  border: 1px dashed #DCD9D9;
}
.format {
    color: #999999 !important;
}
form input[type=file]{
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}
.upload-instruction{
  color: #000;
  text-align: center;
  position: absolute;
  top: 40%;
  bottom: 40%;
  right: 0;
  left: 0
}
.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}
.text-primary {
  color: var(--primary) !important
}
</style>
